/**
 * GameClipsManager - Admin component for managing game clips on completed broadcasts
 */
import React, { useState } from 'react';
import { Plus, Trash2, Play, Youtube, Upload, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GameClipsManager = ({ broadcastId, clips = [], homeTeam, awayTeam, onClipsUpdated }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newClip, setNewClip] = useState({
    title: '',
    team_id: '',
    video_url: '',
    video_file: null
  });
  const [uploadType, setUploadType] = useState('youtube'); // 'youtube' or 'upload'

  const getAuthHeader = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleAddClip = async () => {
    if (!newClip.title) {
      alert('Please enter a clip title');
      return;
    }

    if (uploadType === 'youtube' && !newClip.video_url) {
      alert('Please enter a YouTube URL');
      return;
    }

    if (uploadType === 'upload' && !newClip.video_file) {
      alert('Please select a video file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', newClip.title);
      formData.append('team_id', newClip.team_id || '');

      if (uploadType === 'youtube') {
        formData.append('video_url', newClip.video_url);
      } else {
        formData.append('video_file', newClip.video_file);
      }

      await axios.post(
        `${API}/api/broadcasts/schedule/${broadcastId}/clips`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Reset form
      setNewClip({ title: '', team_id: '', video_url: '', video_file: null });
      setIsAdding(false);
      
      // Refresh clips
      if (onClipsUpdated) onClipsUpdated();
    } catch (error) {
      console.error('Error adding clip:', error);
      alert('Failed to add clip: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClip = async (clipId) => {
    if (!window.confirm('Delete this clip?')) return;

    try {
      await axios.delete(
        `${API}/api/broadcasts/schedule/${broadcastId}/clips/${clipId}`,
        { headers: getAuthHeader() }
      );
      
      if (onClipsUpdated) onClipsUpdated();
    } catch (error) {
      console.error('Error deleting clip:', error);
      alert('Failed to delete clip');
    }
  };

  const getTeamName = (teamId) => {
    if (!teamId || teamId === '') return 'No Team';
    if (homeTeam?.abbreviation === teamId) return homeTeam.name || homeTeam.abbreviation;
    if (awayTeam?.abbreviation === teamId) return awayTeam.name || awayTeam.abbreviation;
    return teamId;
  };

  return (
    <div className="mt-6 border-t border-white/10 pt-6" data-testid="game-clips-manager">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white font-bold text-sm uppercase flex items-center gap-2">
          <Play className="w-4 h-4 text-orange-500" />
          Game Clips ({clips.length})
        </h4>
        
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded transition-colors"
            data-testid="add-clip-btn"
          >
            <Plus className="w-4 h-4" />
            Add Clip
          </button>
        )}
      </div>

      {/* Add Clip Form */}
      {isAdding && (
        <div className="bg-zinc-800/50 border border-white/10 rounded-lg p-4 mb-4" data-testid="add-clip-form">
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-white font-medium">Add New Clip</h5>
            <button onClick={() => setIsAdding(false)} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="block text-zinc-400 text-xs uppercase mb-1">Title</label>
            <input
              type="text"
              value={newClip.title}
              onChange={(e) => setNewClip({ ...newClip, title: e.target.value })}
              placeholder="e.g., Smith 3-pointer to win the game"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 rounded text-sm"
              data-testid="clip-title-input"
            />
          </div>

          {/* Team Selection */}
          <div className="mb-4">
            <label className="block text-zinc-400 text-xs uppercase mb-1">Team (Optional)</label>
            <select
              value={newClip.team_id}
              onChange={(e) => setNewClip({ ...newClip, team_id: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 rounded text-sm"
              data-testid="clip-team-select"
            >
              <option value="">No Team</option>
              {homeTeam && (
                <option value={homeTeam.abbreviation || homeTeam.name}>
                  {homeTeam.name || homeTeam.abbreviation} (Home)
                </option>
              )}
              {awayTeam && (
                <option value={awayTeam.abbreviation || awayTeam.name}>
                  {awayTeam.name || awayTeam.abbreviation} (Away)
                </option>
              )}
            </select>
          </div>

          {/* Upload Type Toggle */}
          <div className="mb-4">
            <label className="block text-zinc-400 text-xs uppercase mb-2">Video Source</label>
            <div className="flex gap-2">
              <button
                onClick={() => setUploadType('youtube')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded text-sm font-medium transition-colors ${
                  uploadType === 'youtube'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                <Youtube className="w-4 h-4" />
                YouTube Link
              </button>
              <button
                onClick={() => setUploadType('upload')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded text-sm font-medium transition-colors ${
                  uploadType === 'upload'
                    ? 'bg-orange-500 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
            </div>
          </div>

          {/* YouTube URL Input */}
          {uploadType === 'youtube' && (
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs uppercase mb-1">YouTube URL</label>
              <input
                type="url"
                value={newClip.video_url}
                onChange={(e) => setNewClip({ ...newClip, video_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 rounded text-sm"
                data-testid="clip-youtube-input"
              />
            </div>
          )}

          {/* File Upload */}
          {uploadType === 'upload' && (
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs uppercase mb-1">Video File (.mp4, .mov)</label>
              <input
                type="file"
                accept=".mp4,.mov,video/mp4,video/quicktime"
                onChange={(e) => setNewClip({ ...newClip, video_file: e.target.files?.[0] || null })}
                className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 rounded text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-orange-500 file:text-white file:text-sm file:cursor-pointer"
                data-testid="clip-file-input"
              />
              {newClip.video_file && (
                <p className="text-zinc-500 text-xs mt-1">
                  Selected: {newClip.video_file.name} ({(newClip.video_file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleAddClip}
            disabled={uploading}
            className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 text-white font-bold rounded transition-colors flex items-center justify-center gap-2"
            data-testid="save-clip-btn"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Clip
              </>
            )}
          </button>
        </div>
      )}

      {/* Existing Clips List */}
      {clips.length > 0 ? (
        <div className="space-y-2">
          {clips.map((clip) => (
            <div
              key={clip.id}
              className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded-lg"
              data-testid={`clip-item-${clip.id}`}
            >
              {/* Thumbnail */}
              <div className="w-24 h-14 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
                {clip.thumbnail_url ? (
                  <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-zinc-600" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{clip.title}</p>
                <p className="text-zinc-500 text-xs flex items-center gap-2">
                  {clip.is_youtube ? (
                    <Youtube className="w-3 h-3 text-red-500" />
                  ) : (
                    <Upload className="w-3 h-3 text-orange-500" />
                  )}
                  {getTeamName(clip.team_id)}
                </p>
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDeleteClip(clip.id)}
                className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                data-testid={`delete-clip-${clip.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-zinc-500 text-sm text-center py-4">
          No clips added yet. Add highlights from this game!
        </p>
      )}
    </div>
  );
};

export default GameClipsManager;
